// Requester-typed alias -> bare Amazon login. Pure, no imports (node-testable).
// Mirrors the validator's normalize_alias / normalize_alias_list (the server re-applies the same
// rule; this copy only shows the requester what will be submitted). Shared cases + parity test:
// _prodcmp/aliasfix/fixtures/alias_cases.json, _prodcmp/aliasfix/test_fe_alias.mjs.

const EDGE = /^[ \t\r\n\u00a0'"`,;<>]+|[ \t\r\n\u00a0'"`,;<>]+$/g;

// One alias: trim; 'Name <alias@x>' -> the bracketed part; strip surrounding quotes/commas/
// semicolons/angle brackets; drop leading '@'s; drop everything from the first '@' (any domain,
// typos too, e.g. 'abhimige@amzon.com'); lowercase. A value that is still not a login (e.g. 'a b')
// is returned as-is (lowercased) so the existing format checks reject it.
export function normalizeAlias(value) {
    if (value === null || value === undefined) return '';
    let a = String(value).trim();
    if (!a) return '';
    const m = a.match(/<([^<>]*)>/);
    if (m) a = m[1];
    a = a.replace(EDGE, '').replace(/^@+/, '');
    const at = a.indexOf('@');
    if (at !== -1) a = a.slice(0, at);
    return a.replace(EDGE, '').toLowerCase();
}

// List form: a string (',' ';' newline CR tab separate entries) or an array of strings.
// 'Name <alias@x>' entries keep only the bracketed part, so a quoted display name containing a
// comma does not split. Normalised, empty entries dropped, de-duplicated, first-seen order.
export function normalizeAliasList(value) {
    const parts = Array.isArray(value)
        ? value.filter(v => v !== null && v !== undefined).map(String)
        : [String(value === null || value === undefined ? '' : value)];
    const seen = new Set();
    const out = [];
    for (const p of parts) {
        const q = p.replace(/(?:"[^"]*"|[^,;\n\r\t<>]*)\s*<([^<>]*)>/g, '\n$1\n');
        for (const seg of q.split(/[,;\n\r\t]+/)) {
            const a = normalizeAlias(seg);
            if (a && !seen.has(a)) { seen.add(a); out.push(a); }
        }
    }
    return out;
}

// The normalised list as one canonical string: "a, b, c".
export function formatAliasList(value) {
    return normalizeAliasList(value).join(', ');
}

// Text form for user-list inputs (mirrors normalize_alias_list_text): the text unchanged when every
// entry is already a bare, unique login (clean input stays exactly as typed), else the normalised
// list joined by `sep`. Arrays are always joined.
export function normalizeAliasListText(value, sep = ', ') {
    const norm = normalizeAliasList(value);
    if (Array.isArray(value)) return norm.join(sep);
    const raw = String(value === null || value === undefined ? '' : value).split(/[,;\n\r\t]+/).map(s => s.trim()).filter(Boolean);
    const clean = raw.length === norm.length && raw.every((s, i) => s === norm[i]);
    return clean ? value : norm.join(sep);
}
